import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  NearMeOutlined, GestureOutlined, CloseOutlined,
  CropSquareOutlined, RadioButtonUncheckedOutlined,
} from '@mui/icons-material';

const PRIMARY = '#1075BA';

const TOOLS = [
  { key: 'pointer', label: 'Pointer — select & move',             icon: <NearMeOutlined sx={{ fontSize: 18 }} /> },
  { key: 'pen',     label: 'Pen — draw bezier shapes (P)',         icon: <GestureOutlined sx={{ fontSize: 18 }} /> },
  { key: 'rect',    label: 'Rectangle — draw rectangular hotspot', icon: <CropSquareOutlined sx={{ fontSize: 18 }} /> },
  { key: 'circle',  label: 'Circle — draw circular hotspot',       icon: <RadioButtonUncheckedOutlined sx={{ fontSize: 18 }} /> },
];

/**
 * Bottom floating toolbar — white theme.
 *
 * Default mode  (hotspotContent = null): tool buttons (pointer/pen/rect/circle)
 * Hotspot mode  (hotspotContent = node): hotspot strip — type, style, destination
 *
 * Props:
 *   activeTool     — 'pointer'|'pen'|'rect'|'circle'
 *   onSelectTool   — (key) => void
 *   penPoints      — number of placed pen points
 *   onDiscardPen   — () => void
 *   rightSlot      — optional ReactNode at right end
 *   hotspotContent — ReactNode | null — replaces tool buttons when provided
 */
export function CanvasToolbar({
  activeTool,
  onSelectTool,
  penPoints = 0,
  onDiscardPen,
  rightSlot,
  hotspotContent = null,
}) {
  const isDrawing   = activeTool === 'rect' || activeTool === 'circle';
  const hotspotMode = !!hotspotContent;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        px: 1,
        pointerEvents: 'none',
      }}
    >
      {/* ── Pill ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: '4px',
          background: '#fff',
          border: '1px solid #e4e9f0',
          borderRadius: '12px',
          px: 1, py: '6px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.04)',
          userSelect: 'none',
          pointerEvents: 'all',
          minHeight: 46,
        }}
      >
        {hotspotMode ? (
          /* ── Hotspot strip contents ─────────────────────────────────── */
          hotspotContent
        ) : (
          /* ── Default tool buttons ───────────────────────────────────── */
          <>
            {TOOLS.map(tool => {
              const isActive = activeTool === tool.key;
              return (
                <Tooltip key={tool.key} title={tool.label} placement="top">
                  <Box
                    onClick={() => onSelectTool(tool.key)}
                    sx={{
                      width: 34, height: 34, borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      color: isActive ? '#fff' : '#6b7585',
                      background: isActive ? PRIMARY : 'transparent',
                      boxShadow: isActive ? `0 0 0 1px ${alpha(PRIMARY, 0.5)}` : 'none',
                      transition: 'all 0.15s',
                      '&:hover': {
                        color: isActive ? '#fff' : PRIMARY,
                        background: isActive ? PRIMARY : alpha(PRIMARY, 0.07),
                      },
                    }}
                  >
                    {tool.icon}
                  </Box>
                </Tooltip>
              );
            })}

            {/* Discard button — pen mid-path */}
            {activeTool === 'pen' && penPoints > 0 && (
              <>
                <Box sx={{ width: 1, height: 20, background: '#e8edf2', mx: 0.5 }} />
                <Tooltip title="Discard current path (Esc)" placement="top">
                  <Box
                    onClick={onDiscardPen}
                    sx={{
                      width: 34, height: 34, borderRadius: '8px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', color: '#e55',
                      transition: 'all 0.15s',
                      '&:hover': { color: '#dc2626', background: '#fff0f0' },
                    }}
                  >
                    <CloseOutlined sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>
              </>
            )}

            {/* Hint text — pen / shape draw */}
            {(activeTool === 'pen' || isDrawing) && (
              <Box sx={{
                ml: 0.5, mr: 0.5, fontSize: '0.62rem', fontWeight: 600,
                color: '#b0bac8', whiteSpace: 'nowrap', letterSpacing: '0.03em',
              }}>
                {activeTool === 'pen'
                  ? penPoints === 0
                    ? 'Click to place points'
                    : penPoints < 3
                    ? `${penPoints} pt${penPoints > 1 ? 's' : ''} — need 3 to close`
                    : `${penPoints} pts — click 1st point to close`
                  : activeTool === 'rect'
                  ? 'Click & drag to draw a rectangle'
                  : 'Click & drag to draw a circle'}
              </Box>
            )}
          </>
        )}
      </Box>

    </Box>
  );
}